import { useCallback } from 'react';

import { buildSchedulePayload } from '@futrli/views/utils/reports/buildSchedulePayload';
import { CreateReportSchedule, ReportScheduleItem } from '@futrli-sdk/report_scheduling';
import { WorkspaceMember } from '@futrli-sdk/people';

import { Recurrence, RecurrenceEndType, RecurrenceWeekDay, useRecurrenceState } from './useRecurrenceState';
import { useScheduleEmailState } from './useScheduleEmailState';
import { FIELD_IDS, useScheduleValidation } from './useScheduleValidation';

export { Recurrence, RecurrenceEndType, RecurrenceWeekDay };

interface UseScheduleReportFormParams {
  reportId: string;
  reportName: string;
  initialSchedule?: ReportScheduleItem;
  members: WorkspaceMember[];
}

/**
 * Top-level form hook that composes recurrence state, email state, validation, and payload building
 * into a single unified API consumed by the schedule report sidebar screens.
 * Maintains the same public interface (`ScheduleReportFormState`) as the original monolithic hook.
 */
export const useScheduleReportForm = ({
  reportId,
  reportName,
  initialSchedule,
  members
}: UseScheduleReportFormParams) => {
  const recurrenceState = useRecurrenceState({ initialSchedule, members });
  const emailState = useScheduleEmailState({ reportName, initialSchedule, members });

  const { validationErrors, validate, revalidate } = useScheduleValidation({
    recurrence: recurrenceState.recurrence,
    recurrenceStartDateError: recurrenceState.recurrenceStartDateError,
    recurrenceRepeatValue: recurrenceState.recurrenceRepeatValue,
    selectedUsers: emailState.selectedUsers,
    emailSubject: emailState.emailSubject,
    emailBody: emailState.emailBody
  });

  const buildPayload = useCallback((): Partial<CreateReportSchedule> | null => {
    if (!validate()) {
      return null;
    }

    return buildSchedulePayload({
      reportId,
      reportName,
      recurrence: recurrenceState.recurrence,
      recurrenceStartDate: recurrenceState.recurrenceStartDate,
      recurrenceRepeatValue: recurrenceState.recurrenceRepeatValue,
      recurrenceEndType: recurrenceState.recurrenceEndType,
      occurancesValue: recurrenceState.occurancesValue,
      recurrenceEndDate: recurrenceState.recurrenceEndDate,
      recurrenceWeekDay: recurrenceState.recurrenceWeekDay,
      recurrenceMonthDay: recurrenceState.recurrenceMonthDay,
      selectedUsers: emailState.selectedUsers,
      emailSubject: emailState.emailSubject,
      emailBody: emailState.emailBody,
      reviewBeforeSending: emailState.reviewBeforeSending
    });
  }, [
    validate,
    reportId,
    reportName,
    recurrenceState.recurrence,
    recurrenceState.recurrenceStartDate,
    recurrenceState.recurrenceRepeatValue,
    recurrenceState.recurrenceEndType,
    recurrenceState.occurancesValue,
    recurrenceState.recurrenceEndDate,
    recurrenceState.recurrenceWeekDay,
    recurrenceState.recurrenceMonthDay,
    emailState.selectedUsers,
    emailState.emailSubject,
    emailState.emailBody,
    emailState.reviewBeforeSending
  ]);

  return {
    // Recurrence state
    ...recurrenceState,

    // Email state
    ...emailState,

    // Validation
    validationErrors,
    validate,
    revalidate,
    FIELD_IDS,

    // Submit
    buildPayload
  };
};

export type ScheduleReportFormState = ReturnType<typeof useScheduleReportForm>;
